import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { Form } from 'semantic-ui-react';
import * as yup from 'yup';
import { AppLayout, Pane, SettingsLayout } from '../../../../components/layout';
import { SettingMenuItem } from '../../../../components/layout/SettingsLayout/SettingsMenu';
import {
  Avatar,
  PrimaryButton,
} from '../../../../components/layout/SettingsLayout/styles';
import {
  AVAILABILITY_PREFERENCE,
  GENDERS,
  INTERESTS,
  PLACEHOLDER_IMAGE,
  STATES,
  VALIDATION_MESSAGES,
} from '../../../../config/constants';
import { COACH_TABS } from '../../../../constants/tabs';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  updateProfilePhoto,
  updateUser,
} from '../../../../redux/user/user.saga';
import {
  userProfileSelector,
  userSelector,
} from '../../../../redux/user/user.selectors';

type FormData = {
  name: string;
  bio: string;
  gender: string;
  homeCity: string;
  homeState: string;
  college: string;
  major: string;
  graduateSchool: string;
  degree: string;
  mbti: string;
  interests: any;
  days: any;
  sessionLength: any;
  photo: string;
};

const schema = yup.object().shape({
  name: yup.string().required(VALIDATION_MESSAGES.required),
  interests: yup.array().min(1, VALIDATION_MESSAGES.required),
  sessionLength: yup.array().min(1, VALIDATION_MESSAGES.required),
  days: yup.array().min(1, VALIDATION_MESSAGES.required),
});

interface Photo extends File {
  preview: string;
}

const Profile: React.FC = () => {
  const history = useHistory();
  const user = useAppSelector(userSelector);
  const { status } = user;
  const userProfile = useAppSelector(userProfileSelector);
  const dispatch = useAppDispatch();

  const [files, setFiles] = useState<Photo[]>([]);
  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/*',
    onDrop: (acceptedFiles: File[]) => {
      setFiles(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        )
      );
    },
  });
  const { handleSubmit, control, formState } = useForm<FormData>({
    defaultValues: {
      ...userProfile,
      ...user.availability,
      interests: user.interests,
    },
    resolver: yupResolver(schema),
  });
  const { errors } = formState;

  const getPhotoImage = () => {
    if (files.length > 0) {
      return files[0].preview;
    }
    return userProfile.photo || PLACEHOLDER_IMAGE;
  };

  const onFormSubmit = (data: FormData) => {
    dispatch(
      updateUser.action({
        data: {
          'profile.name': data.name,
          'profile.gender': data.gender,
          'profile.bio': data.bio,
          'profile.homeCity': data.homeCity,
          'profile.homeState': data.homeState,
          'profile.college': data.college,
          'profile.major': data.major,
          'profile.graduateSchool': data.graduateSchool,
          'profile.degree': data.degree,
          'profile.mbti': data.mbti,
          'availability.sessionLength': data.sessionLength,
          'availability.days': data.days,
          interests: data.interests,
        },
      })
    );

    if (files.length > 0) {
      dispatch(updateProfilePhoto.action(files[0]));
    }
  };

  const handleNavigationChange = (item: SettingMenuItem) => {
    history.push(item.name);
  };

  return (
    <AppLayout page="settings">
      <SettingsLayout
        items={COACH_TABS}
        selectedItem={{ name: 'profile' }}
        onSelectItem={handleNavigationChange}>
        <Form onSubmit={handleSubmit(onFormSubmit)}>
          <Pane
            display="flex"
            justifyContent="center"
            {...getRootProps({ refKey: 'ref' })}>
            <input {...getInputProps()} />
            <Avatar src={getPhotoImage()} size="small" circular />
          </Pane>
          <Pane height={25} />
          <Controller
            control={control}
            name="name"
            render={({ field: { ref, ...field } }) => (
              <Form.Input
                label="Name"
                error={errors?.name?.message}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="bio"
            render={({ field: { ref, ...field } }) => (
              <Form.TextArea
                label="Bio"
                placeholder="Tell us more about you..."
                error={errors?.bio?.message}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="gender"
            render={({ field }) => (
              <Form.Select
                label="Gender"
                fluid
                error={errors?.gender?.message}
                options={GENDERS.map((gender) => ({
                  value: gender,
                  text: gender,
                }))}
                {...field}
                onChange={(e, { value }) => {
                  field.onChange(value);
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="homeCity"
            render={({ field: { ref, ...field } }) => (
              <Form.Input
                label="Home City"
                error={errors?.homeCity?.message}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="homeState"
            render={({ field }) => (
              <Form.Select
                label="Home State"
                fluid
                error={errors?.homeState?.message}
                options={Object.keys(STATES).map((val) => ({
                  value: val,
                  text: STATES[val],
                }))}
                {...field}
                onChange={(e, { value }) => {
                  field.onChange(value);
                }}
              />
            )}
          />
          <Form.Group widths="equal">
            <Controller
              control={control}
              name="college"
              render={({ field: { ref, ...field } }) => (
                <Form.Input
                  label="College"
                  error={errors?.college?.message}
                  {...field}
                />
              )}
            />
            <Controller
              control={control}
              name="graduateSchool"
              render={({ field: { ref, ...field } }) => (
                <Form.Input
                  label="Graduate School"
                  error={errors?.graduateSchool?.message}
                  {...field}
                />
              )}
            />
          </Form.Group>
          <Form.Group widths="equal">
            <Controller
              control={control}
              name="major"
              render={({ field: { ref, ...field } }) => (
                <Form.Input
                  label="Major"
                  error={errors?.major?.message}
                  {...field}
                />
              )}
            />
            <Controller
              control={control}
              name="degree"
              render={({ field: { ref, ...field } }) => (
                <Form.Input
                  label="Degree"
                  error={errors?.degree?.message}
                  {...field}
                />
              )}
            />
          </Form.Group>
          <Controller
            control={control}
            name="mbti"
            render={({ field: { ref, ...field } }) => (
              <Form.Input
                label="MBTI"
                error={errors?.mbti?.message}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="interests"
            render={({ field }) => (
              <Form.Dropdown
                label="Interests"
                fluid
                selection
                multiple
                error={errors?.interests?.message}
                options={INTERESTS.map((val) => ({
                  value: val,
                  text: val,
                }))}
                {...field}
                onChange={(e, { value }) => {
                  field.onChange(value);
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="sessionLength"
            render={({ field }) => (
              <Form.Dropdown
                label="Availability: Session time preference"
                fluid
                selection
                multiple
                error={errors?.sessionLength?.message}
                options={AVAILABILITY_PREFERENCE.sessionDurations.map(
                  (val) => ({
                    value: val,
                    text: val,
                  })
                )}
                {...field}
                onChange={(e, { value }) => {
                  field.onChange(value);
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="days"
            render={({ field }) => (
              <Form.Dropdown
                label="Availability: Weekend or Weekdays?"
                fluid
                selection
                multiple
                error={errors?.days?.message}
                options={AVAILABILITY_PREFERENCE.daysOfWeek.map((val) => ({
                  value: val,
                  text: val,
                }))}
                {...field}
                onChange={(e, { value }) => {
                  field.onChange(value);
                }}
              />
            )}
          />
          <Pane height={32} />
          <PrimaryButton
            loading={status === 'loading'}
            disabled={status === 'loading'}
            primary
            type="submit">
            Save
          </PrimaryButton>
        </Form>
      </SettingsLayout>
    </AppLayout>
  );
};

export default Profile;
